import React, {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { batch, useDispatch } from 'react-redux';
import { InterPoint, Logger } from '@saviynt/common';
import {
  ApplicationIcon,
  Box,
  ButtonIcon,
  Icon,
  ListRow,
  ListRowGroup,
  Menu,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodColumnChip,
  SodListLoader,
  SodMessage,
  SodReloader,
  SodRiskPriorityChip,
} from '../../../../../../../shared/components';
import {
  ABORT_ERROR,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_MED,
  ENTITLEMENT_DETAILS_TABS,
  SEARCH_TEXT_LENGTH,
  testIds,
  violationsEntitlement,
} from '../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useInViewObserver,
  useNavigateTo,
  useTypedSelector,
} from '../../../../../../../shared/hooks';
import { type ListTrackerState } from '../../../../../../../shared/types';
import { createDevRejectionHandler } from '../../../../../../../shared/utils';
import { type EntKebabOptions } from '../../../../../../ViolationsEntitlement/data-store';
import {
  type EntitlementRisksSidePanelGroupRowContextState,
  type EntitlementRisksSidePanelRowContextState,
} from '../../../../../data-layer/detailsTypes';
import { detailsActions } from '../../../../../data-store/detailsSlice';
import {
  type GetInherentRisksRequest,
  type RiskItem,
  useGetInherentRisksMutation,
} from '../../data-layer';
import { type TrackedRiskRowType } from '../../hooks';
import {
  configureRisksTracker,
  getInherentRisksListPayload,
  getRiskRowContext,
} from '../../utils';

import './InherentRisksList.css';

const MAX_COUNT = 25;
const LEAVE_ANIM_DURATION = 600;

interface InherentRisksListProps {
  riskIds: number[];
  entKeys: number[];
}

/**
 * Entitlement Inherent Risks Main Component
 * Main container component for displaying and managing inherent risks associated with entitlements
 *
 * @component
 * @description Orchestrates the inherent risks feature including data fetching, filtering, search,
 * list display, and side panel interactions. Integrates with multiple APIs and manages complex
 * state for risk data presentation and user interactions.
 *
 * @returns {ReactElement} Memoized main risks component with full feature set
 */
function InherentRisksList({
  riskIds,
  entKeys,
}: InherentRisksListProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const initialOptionsWithIcons = useMemo(
    () => [
      {
        label: l('View Violations'),
        value: ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
        icon: 'SOD',
      },
      {
        label: l('View Details'),
        value: ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY,
        icon: 'ArrowRight',
      },
    ],
    [l]
  );

  const {
    aag: {
      details: {
        activeTab,
        entitlementRisks: {
          list: { totalRecords: risksTotalRecords, rowIdToTransition },
          controls: { activeFilters, searchKey, activeColumns, sortInAction },
          sidepanel: { isSidePanelVisible },
        },
      },
      global: { isGlobalLoading },
    },
  } = useTypedSelector((state) => state);

  // API endpoints
  const [getInherentRisks, { isLoading: isListLoading }] =
    useGetInherentRisksMutation();
  const { navigateToDetails } = useNavigateTo();

  // Offset, observer and lists
  const abortRef = useRef<((reason: string) => void) | null>(null);
  const requestInProgressRef = useRef<boolean>(false);

  // Infinite scroll state
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isObserverInView, setIsObserverInView] = useState(true);
  const [isListError, setIsListError] = useState(false);
  const [listRows, setListRows] = useState([] as TrackedRiskRowType[]);
  const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null);

  const listObserverElement = useInViewObserver(({ isInView }) => {
    setIsObserverInView(isInView);
  });

  // Constants for infinite scroll
  const isValidApiCall = useMemo(
    () =>
      Boolean(
        risksTotalRecords === null ||
          (typeof risksTotalRecords === 'number' &&
            currentOffset < risksTotalRecords &&
            listRows.length < risksTotalRecords)
      ),
    [currentOffset, risksTotalRecords, listRows.length]
  );
  const isListZero = useMemo(
    () => Boolean(listRows.length === 0),
    [listRows.length]
  );
  const isZeroCount = useMemo(
    () => Boolean(isListZero && risksTotalRecords !== null && !isValidApiCall),
    [risksTotalRecords, isListZero, isValidApiCall]
  );
  const isSearchActive = useMemo(
    () => Boolean(searchKey !== null && searchKey.length >= SEARCH_TEXT_LENGTH),
    [searchKey]
  );
  const isFilterActive = useMemo(
    () => Boolean(activeFilters.length > 0),
    [activeFilters]
  );

  useEffect(() => {
    dispatch(detailsActions.setEntitlementRisksLoading(isListLoading));
  }, [dispatch, isListLoading]);

  // Extract complex expressions for dependency array
  const riskIdsString = useMemo(() => JSON.stringify(riskIds), [riskIds]);
  const entKeysString = useMemo(() => JSON.stringify(entKeys), [entKeys]);

  // List API call
  const handleListApiCall = useCallback(
    async (offset: number) => {
      // Prevent duplicate requests
      if (requestInProgressRef.current) {
        return Promise.reject(new Error('Request already in progress'));
      }

      requestInProgressRef.current = true;
      abortRef.current?.('New Search');

      // Map for the active options for different filters
      const activeFiltersMap = Object.fromEntries(
        activeFilters.map((f) => [f.value, f.options.map((o) => o.value)])
      );

      const getFilterPayload = getInherentRisksListPayload(
        activeFiltersMap,
        sortInAction,
        { max: MAX_COUNT, offset }
      );
      const payload: GetInherentRisksRequest = {
        includeDetails: true,
        includeViolationSummary: false,
        riskKeys: riskIds,
        entitlementKeys: entKeys,
        columnToSort: getFilterPayload.sort.field,
        globalSearch: searchKey ?? '',
        max: getFilterPayload.pagination.max,
        offset: getFilterPayload.pagination.offset,
        order: getFilterPayload.sort.order,
        ...getFilterPayload.filters,
      };

      try {
        const controller = getInherentRisks(payload);

        abortRef.current = () => {
          controller.abort();
          requestInProgressRef.current = false;
        };

        const response = (await controller.unwrap()) as {
          totalRecords: number;
          data: RiskItem[] | null | undefined;
        };

        requestInProgressRef.current = false;

        return response;
      } catch (error) {
        requestInProgressRef.current = false;

        if ((error as { name?: string }).name !== ABORT_ERROR) {
          setIsListError(true);
          setIsObserverInView(false);

          throw error;
        }

        throw Error(ABORT_ERROR);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getInherentRisks,
      searchKey,
      sortInAction,
      activeFilters,
      riskIdsString,
      entKeysString,
    ]
  );

  // Infinite scroll effect
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (
        !isObserverInView ||
        !isValidApiCall ||
        isGlobalLoading ||
        riskIds.length === 0
      ) {
        return;
      }

      handleListApiCall(currentOffset)
        .then((response) => {
          setIsObserverInView(false);
          setCurrentOffset((prev) => prev + MAX_COUNT);
          setIsListError(false);

          const { totalRecords, data } = response;

          if (!data || data.length === 0) {
            dispatch(
              detailsActions.setEntitlementRisksTotalRecords(totalRecords || 0)
            );

            return;
          }

          let newRows: TrackedRiskRowType[];

          try {
            newRows = configureRisksTracker(data) as TrackedRiskRowType[];
          } catch (error) {
            Logger.error('Error in data transformation:', error, 'Data:', data);

            return;
          }

          setListRows((prev) => [
            ...prev.map((row) => ({
              ...row,
              tracker: {
                ...row.tracker,
                isFirstAddded: false,
              },
            })),
            ...newRows,
          ]);
          dispatch(
            detailsActions.setEntitlementRisksTotalRecords(totalRecords)
          );
        })
        .catch(
          createDevRejectionHandler('InherentRisksList: handleListApiCall')
        );
    }, 100); // Increased debounce time to prevent rapid calls

    return () => clearTimeout(debounceTimer);
  }, [
    dispatch,
    isGlobalLoading,
    isObserverInView,
    isValidApiCall,
    currentOffset,
    handleListApiCall,
    riskIds.length,
  ]);

  // Resetting list and offset to default so that observer is visible
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (!isGlobalLoading) {
        batch(() => {
          dispatch(detailsActions.setEntitlementRisksTotalRecords(null));
          dispatch(
            detailsActions.setEntitlementRisksSidePanelRowContext({
              isSidePanelVisible: false,
              rowContext: {} as EntitlementRisksSidePanelGroupRowContextState,
            })
          );
        });
        setListRows([]);
        setIsListError(false);
        setCurrentOffset(0);
        setIsObserverInView(true);
      }
    }, 20);

    return () => clearTimeout(debounceTimer);
  }, [
    // Add all the dependencies based on which you want to refresh the page
    dispatch,
    isGlobalLoading,
    activeFilters,
    searchKey,
    sortInAction,
  ]);

  // Removing connectors if any when `list` mode and sidepanel closed
  useEffect(() => {
    if (!isSidePanelVisible) {
      setListRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        }))
      );
    }
  }, [isSidePanelVisible]);

  // Close sidepanel when global loading starts (indicates tab/page change)
  useEffect(() => {
    if (isSidePanelVisible) {
      dispatch(detailsActions.setSidePanelClose());
      dispatch(
        detailsActions.setEntitlementRisksSidePanelRowContext({
          isSidePanelVisible: false,
          rowContext: {} as EntitlementRisksSidePanelGroupRowContextState,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Should not update on these params, one time exectution
  }, [dispatch, isGlobalLoading, activeTab]);

  // Sidepanel handling and row connector lgic on `row` click
  const handleRowClick = useCallback(
    (id: string) => {
      let modifiedIndex: number | undefined;
      const rowContext: EntitlementRisksSidePanelGroupRowContextState = {
        current: null as EntitlementRisksSidePanelRowContextState | null,
      } as EntitlementRisksSidePanelGroupRowContextState;
      const listLength = listRows.length;

      const modifiedRows = listRows.map((row, index) => {
        if (row.tracker.id === id) {
          modifiedIndex = index;

          return {
            ...row,
            tracker: {
              ...row.tracker,
              isRowDetailsOpen: true,
            },
          };
        }

        return {
          ...row,
          tracker: {
            ...row.tracker,
            isRowDetailsOpen: false,
          },
        };
      });

      if (modifiedIndex !== undefined) {
        // Will always have the current row
        const { tracker: currentTracker, ...currentRowData } =
          modifiedRows[modifiedIndex];

        rowContext.current = getRiskRowContext(currentRowData as RiskItem);

        if (modifiedIndex < listLength - 1 && listLength > 1) {
          const { tracker: nextTracker, ...nextRowData } =
            modifiedRows[modifiedIndex + 1];

          rowContext.next = getRiskRowContext(nextRowData as RiskItem);
        }

        setListRows(() => modifiedRows);

        dispatch(
          detailsActions.setEntitlementRisksSidePanelRowContext({
            isSidePanelVisible: true,
            rowContext,
          })
        );

        if (!isSidePanelVisible) {
          dispatch(detailsActions.setIsSidePanelVisible(!isSidePanelVisible));
        }
      }
    },
    [dispatch, isSidePanelVisible, listRows]
  );

  // Transition logic here
  useEffect(() => {
    if (rowIdToTransition !== null) {
      const nextItem: { tracker?: ListTrackerState } = {};
      const listRowsLength = listRows.length;

      // Start the transition
      setListRows((prevListRows) =>
        prevListRows.map((row, index) => {
          if (row.tracker.id === rowIdToTransition) {
            // Check if next available item is present
            if (index !== listRowsLength - 1 && row.tracker.isRowDetailsOpen) {
              nextItem.tracker = listRows[index + 1].tracker;
            }

            return {
              ...row,
              tracker: { ...row.tracker, isTransition: true },
            };
          }

          return row;
        })
      );

      // End the transition and manage the next item with sidepanel
      setTimeout(() => {
        setListRows((prevListRows) =>
          prevListRows
            .filter((row) => row.tracker.id !== rowIdToTransition)
            .map((row) => {
              if (nextItem.tracker && row.tracker.id === nextItem.tracker.id) {
                return {
                  ...row,
                  tracker: { ...row.tracker, isRowDetailsOpen: true },
                };
              }

              return row;
            })
        );

        dispatch(detailsActions.setEntitlementRisksRowIdToTransition(null));
      }, LEAVE_ANIM_DURATION);
    }
  }, [dispatch, rowIdToTransition, listRows]);

  const getCenterContent = useCallback(
    (item: TrackedRiskRowType, listConfigurableColumns: JSX.Element[]) => {
      const { riskName, riskScore } = item;

      return (
        <Box className='Sod-InherentRisksListGroup-centerContent'>
          <Typography kind='body3' color={CF_SEC_MED}>
            {l('Risk')}
          </Typography>
          <Typography kind='h3' color={CF_NEU_BLD}>
            {riskName}
          </Typography>
          <Box className='Sod-InherentRisksListGroup-rowDetails'>
            {riskScore && riskScore !== 0 && item.riskSeverity ? (
              <>
                <SodRiskPriorityChip
                  key='SodRiskPriorityChip'
                  priority={String(item.riskSeverity)}
                  riskScore={riskScore}
                  isPrefixInterpoint={false}
                />
                <InterPoint color='neutralBold' font='body2' />
              </>
            ) : null}
            {Object.keys(item).filter(
              (key) =>
                key.startsWith('function') &&
                !key.endsWith('Key') &&
                item[key as keyof RiskItem]
            ).length > 0 && (
              <>
                <Typography kind='body2' color={CF_NEU_STL}>
                  {l('Functions:')}
                </Typography>
                <Typography kind='body2' color={CF_NEU_BLD} lineLimit={1}>
                  {Object.keys(item)
                    .filter(
                      (key) =>
                        key.startsWith('function') &&
                        !key.endsWith('Key') &&
                        item[key as keyof RiskItem]
                    )
                    .map((key) => item[key as keyof RiskItem])
                    .join(', ')}
                </Typography>
              </>
            )}
            {listConfigurableColumns.length > 0 && listConfigurableColumns}
          </Box>
        </Box>
      );
    },
    [l]
  );

  // Get entitlement data at component level
  const {
    entitlementSummary: { selectedEntitlement, data: selectedEntitlementData },
  } = useTypedSelector((state) => state.aag.details);

  const getTrailingContent = useCallback(
    (item: TrackedRiskRowType) => {
      const isExpanded = expandedMenuId === item.riskId;

      const menuTrigger = (
        <ButtonIcon
          size='medium'
          kind='ghost'
          ariaLabel={l('More actions')}
          isSelected={isExpanded}
          icon={<Icon kind='MoreVertical' />}
        />
      );

      const handleMenuExpand = (isMenuOpen: boolean) => {
        if (isMenuOpen) {
          setExpandedMenuId(null);
        } else {
          setExpandedMenuId(item.riskId || null);
        }
      };

      const handleSourceChange = (
        sourceType: string,
        tab: string,
        selectedItem: TrackedRiskRowType
      ) => {
        const entitlementDisplayName =
          selectedEntitlement?.entitlementDisplayName ??
          selectedEntitlementData?.entitlementDisplayName;

        const isViolationsTab = tab === ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS;

        navigateToDetails({
          key: String(selectedItem.riskId),
          activeTab: tab,
          isEntitlementRoute: true,
          navigation: {
            source: sourceType,
            title: selectedItem.riskName ?? '',
            ...(isViolationsTab
              ? {
                  entName: entitlementDisplayName ?? '',
                  riskName: selectedItem.riskName ?? '',
                }
              : {}),
          },
        });
      };

      return (
        <Box className='Sod-InherentRisksListGroup-trailingContent'>
          {/* Placeholder Button */}
          {!isSidePanelVisible && (
            <Box className='Sod-InherentRisksListGroup-rowKabobWrapper'>
              <Menu
                options={initialOptionsWithIcons}
                onChange={(e: EntKebabOptions) => {
                  if (e.value === ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY) {
                    // Handle View Details click
                    const { tracker, ...entitlementListData } = item;

                    dispatch(
                      detailsActions.setSelectedEntitlementRisks(
                        entitlementListData as RiskItem
                      )
                    );

                    handleSourceChange(
                      violationsEntitlement,
                      ENTITLEMENT_DETAILS_TABS.TAB_RISKS_SUMMARY,
                      item
                    );
                  } else {
                    // Store selected entitlement for details page
                    const { tracker, ...entitlementListData } = item;

                    dispatch(
                      detailsActions.setSelectedEntitlementRisks(
                        entitlementListData as RiskItem
                      )
                    );

                    handleSourceChange(
                      violationsEntitlement,
                      ENTITLEMENT_DETAILS_TABS.TAB_VIOLATIONS,
                      item
                    );
                  }
                }}
                minOptionsForSearch={10}
                trigger={menuTrigger}
                setIsExpanded={(isMenuOpen: boolean) => {
                  handleMenuExpand(isMenuOpen);
                }}
                placement='bottom-end'
                isPortalEnabled
              />
            </Box>
          )}
        </Box>
      );
    },
    [
      expandedMenuId,
      isSidePanelVisible,
      navigateToDetails,
      dispatch,
      initialOptionsWithIcons,
      l,
      selectedEntitlement,
      selectedEntitlementData,
    ]
  );

  // Clear filter on no data
  const handleClearBtn = useCallback(() => {
    dispatch(detailsActions.setEntitlementRisksActiveFilters([]));
  }, [dispatch]);

  // Render body here
  const renderListBody = useCallback(() => {
    // No data Screens
    if (isZeroCount && !isListLoading) {
      return (
        <SodMessage
          isSearchActive={isSearchActive}
          isFilterActive={isFilterActive}
          handleFilterClearBtn={handleClearBtn}
          defaultTitle={l('No Risks')}
          defaultMessage={l('No Inherent risks found.')}
        />
      );
    }

    return (
      <ListRowGroup className='Sod-InherentRisksList--isListRowGroup'>
        {/* List rows */}
        {listRows.map((row) => {
          const {
            tracker: { id, isRowDetailsOpen, isTransition, isFirstAddded },
          } = row;

          const rowClasses = cx(
            'Sod-InherentRisksList-listRow',
            isRowDetailsOpen && 'Sod-InherentRisksList-listRow--isConnected',
            isTransition && 'Sod-InherentRisksList-listRow--isTransition',
            isFirstAddded && 'Sod-InherentRisksList-listRow--isFirstAddded'
          );

          const selectedColumns = activeColumns.filter(
            (column) => column.isChecked
          );

          const listConfigurableColumns = selectedColumns
            .filter((column) => column.isChecked)
            .map((column, index) => {
              const { label, value } = column;
              const columnValue = row[value as keyof typeof row] ?? null;

              return (
                <React.Fragment key={value}>
                  {index === 0 && (
                    <InterPoint color='neutralBold' font='body2' />
                  )}
                  <SodColumnChip
                    name={label}
                    value={columnValue ? String(columnValue) : 'null'}
                    isPrefixInterpoint={index < selectedColumns.length - 1}
                  />
                </React.Fragment>
              );
            });

          return (
            <ListRow
              key={id}
              className={rowClasses}
              dataTestId={testIds.SOD_ENTITLEMENT_TAB_RISKS_LISTROW}
              isCheckboxDisabled
              leadingComp={
                <ApplicationIcon icon='CriticalHighRiskHexagon' size='medium' />
              }
              centerContent={
                <Box className='Sod-InherentRisksListGroup-centerContent'>
                  {getCenterContent(row, listConfigurableColumns)}
                </Box>
              }
              trailingContent={!isSidePanelVisible && getTrailingContent(row)}
              handleRowClick={() => {
                const { tracker, ...riskData } = row;

                dispatch(
                  detailsActions.setEntitlementRisksRowContext(
                    riskData as RiskItem
                  )
                );
                dispatch(
                  detailsActions.setSelectedEntitlementRisks(
                    riskData as RiskItem
                  )
                );
                handleRowClick(id);
              }}
              isRowDetailsOpen={isRowDetailsOpen}
              connectorDistance='1.125rem'
              isRowConnectTransitionEnabled
            />
          );
        })}
        {/* Observer Element */}
        {!isListError ? listObserverElement : null}
        {/* Loading Skeleton */}
        {!isListError && isValidApiCall && <SodListLoader count={3} />}
        {/* Retry mechanism */}
        {isListError && (
          <SodReloader triggerRefresh={() => setIsListError(false)} />
        )}
      </ListRowGroup>
    );
  }, [
    isZeroCount,
    isSearchActive,
    isFilterActive,
    handleClearBtn,
    l,
    listRows,
    getCenterContent,
    isSidePanelVisible,
    getTrailingContent,
    handleRowClick,
    listObserverElement,
    isListError,
    isValidApiCall,
    activeColumns,
    dispatch,
    isListLoading,
  ]);

  return (
    <Box
      className='Sod-InherentRisksList'
      dataTestId={testIds.SOD_ENTITLEMENT_TAB_RISKS}>
      {renderListBody()}
    </Box>
  );
}

export default InherentRisksList;
